import { createInsertSchema } from "../../utils/sql";
import { withZeroTransaction } from "../../zero/utils";
import { zql } from "../../zero/schema";
import { list } from "../../common/list/list.sql";
import { z } from "zod/v4";
import { Food } from "../food";
import { List } from "@corpuscomplete/core/common/list";
import { shoppingList } from "./shopping-list.sql";
import { accessDenied } from "@corpuscomplete/core/utils";

/**
 * Shopping-list namespace boundary for meal-plan-linked list creation.
 *
 * Invariants:
 * - Food items must be viewable to the acting user before persistence.
 * - Each shopping list is backed by a base list row and optional food item links.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace ShoppingList {
  const createItemSchema = z.discriminatedUnion("type", [
    List.createItemSchema.extend({
      type: z.literal("food"),
      foodServingId: z.uuid(),
      amount: z.number().positive().max(999_999),
    }),
    List.createItemSchema.extend({
      type: z.literal("custom"),
      label: z.string().max(255),
    }),
  ]);

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    createInsertSchema(list).extend({
      // TODO: Should the mealPlanId just be on the high level object?
      shoppingList: createInsertSchema(shoppingList).omit({ id: true }),
      items: createItemSchema.array(),
    }),
    async ({ tx, ctx, args: { items, shoppingList: shoppingListInput, ...listInput } }) => {
      const servingIds = items
        .filter((item): item is Extract<typeof item, { type: "food" }> => item.type === "food")
        .map((item) => item.foodServingId);
      const servings = await tx.run(zql.foodServing.where("id", "IN", [...new Set(servingIds)]));
      const foodIds = servings.map((serving) => serving.foodId);
      const canViewFoods = await Food.canViewBatch(tx, ctx.actor, foodIds);
      if (!canViewFoods) {
        accessDenied("You do not have permission to view the food on this shopping list item");
      }

      // TODO: Group items into sections that correspond to "shopping categories"
      // Strip extra fields from items - List.create only expects listItem fields
      const listItems = items.map((item) => ({
        id: item.id,
        label: item.type === "custom" ? item.label : null,
        note: null,
        sortIndex: item.sortIndex,
      }));

      await List.create.fn({
        tx,
        ctx,
        args: {
          ...listInput,
          sections: [],
          orphanItems: listItems,
        },
      });

      await tx.mutate.shoppingList.insert({
        id: listInput.id,
        mealPlanId: shoppingListInput.mealPlanId,
      });

      await Promise.all(
        items.map(async (item) => {
          if (item.type === "food") {
            await tx.mutate.shoppingListFoodItem.insert({
              id: item.id,
              shoppingListId: listInput.id,
              foodServingId: item.foodServingId,
              amount: item.amount,
            });
          }
        }),
      );
    },
  );

  export function foodItemLabel(foodItem: Food.Item) {
    const { title, subtitle } = Food.formatItem(foodItem);
    return `${subtitle} ${title}`;
  }
}
